import type { PriceList, PriceListItem } from '@erp/shared';
import { Ban, Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button, Combobox, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDecimals, formatMoney } from '../../lib/money.js';
import {
  createPriceList, deactivatePriceList, deletePriceListItem, listPriceListItems, listPriceLists,
  updatePriceList, upsertPriceListItem,
} from '../../lib/pricing.js';

interface CatalogItem { id: string; name: string; code: string; saleCurrencyCode: string | null }

export function PriceListsPage(): JSX.Element {
  const { t } = useTranslation('pricing');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('items.write');
  const [lists, setLists] = useState<PriceList[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<PriceList | null>(null);
  const [pricesFor, setPricesFor] = useState<PriceList | null>(null);

  const reload = async (): Promise<void> => { setLists(await listPriceLists()); };
  useEffect(() => { void reload(); }, []);

  if (!lists) {
    return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-32 w-full" /></Card>;
  }

  const onDeactivate = async (l: PriceList): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmDeactivate', { name: l.name }), tone: 'danger' }))) return;
    try { await deactivatePriceList(l.id); await reload(); } catch { toast.error(tc('errors:server')); }
  };

  const columns: DataTableColumn<PriceList>[] = [
    {
      id: 'name', header: t('columns.name'), sortValue: (l) => l.name, searchValue: (l) => l.name,
      cellClassName: 'font-medium',
      cell: (l) => (
        <button type="button" className="text-fg underline-offset-4 hover:text-brand hover:underline" onClick={() => { setPricesFor(l); setEditing(null); setShowCreate(false); }}>
          {l.name}
        </button>
      ),
    },
    {
      id: 'currency', header: t('columns.currency'), sortValue: (l) => l.currencyCode ?? '',
      cell: (l) => l.currencyCode ?? <span className="text-fg-muted">{t('form.anyCurrency')}</span>,
    },
    {
      id: 'adjustment', header: t('columns.adjustment'),
      cell: (l) => l.adjustmentType === 'percent' && l.adjustmentValue != null
        ? <span className={`font-mono ${Number(l.adjustmentValue) < 0 ? 'text-success' : 'text-warning'}`}>{Number(l.adjustmentValue) > 0 ? '+' : ''}{l.adjustmentValue}%</span>
        : <span className="text-fg-muted">—</span>,
    },
    {
      id: 'items', header: t('columns.items'), align: 'end', sortValue: (l) => l.itemCount ?? 0,
      cellClassName: 'tabular-nums', cell: (l) => l.itemCount ?? 0,
    },
    {
      id: 'status', header: t('columns.status'), sortValue: (l) => (l.isActive ? 1 : 0),
      cell: (l) => <Badge variant={l.isActive ? 'success' : 'secondary'}>{t(`status.${l.isActive ? 'active' : 'inactive'}`)}</Badge>,
    },
    {
      // Surfaced as a column, not left buried in the form: a group being invisible
      // at the counter is the kind of setting someone hunts for when a cashier says
      // a customer "isn't there".
      id: 'pos', header: t('columns.pos'), sortValue: (l) => (l.hideFromPos ? 0 : 1),
      cell: (l) => (l.hideFromPos
        ? <Badge variant="secondary">{t('status.hiddenFromPos')}</Badge>
        : <span className="text-fg-muted">—</span>),
    },
    {
      // On the list, not buried in the form, for the same reason the POS column is:
      // "why did this customer not get cashback" is asked about a GROUP, and the
      // answer should be visible without opening four dialogs.
      id: 'cashback', header: t('columns.cashback'), sortValue: (l) => (l.hasCashback ? 1 : 0),
      cell: (l) => (l.hasCashback
        ? <Badge variant="success">{t('status.cashbackOn')}</Badge>
        : <Badge variant="secondary">{t('status.cashbackOff')}</Badge>),
    },
    {
      id: 'regCashback', header: t('columns.regCashback'), sortValue: (l) => (l.hasRegistrationCashback ? 1 : 0),
      cell: (l) => (l.hasRegistrationCashback
        ? <Badge variant="success">{t('status.cashbackOn')}</Badge>
        : <Badge variant="secondary">{t('status.cashbackOff')}</Badge>),
    },
    ...(canWrite ? [{
      id: 'actions', header: '', align: 'end' as const, headClassName: 'w-52',
      cell: (l: PriceList) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => { setPricesFor(l); setEditing(null); setShowCreate(false); }}>
            <Tag className="h-4 w-4" aria-hidden />{t('actions.prices')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setEditing(l); setShowCreate(false); setPricesFor(null); }} title={t('actions.edit')}>
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          {l.isActive && (
            <Button variant="ghost" size="sm" onClick={() => void onDeactivate(l)} title={t('actions.deactivate')}>
              <Ban className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('description')}</p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => { setShowCreate(true); setPricesFor(null); }}><Plus className="me-1.5 h-4 w-4" />{t('create')}</Button>
        )}
      </header>

      {showCreate && <PriceListForm onCancel={() => setShowCreate(false)} onSaved={async () => { setShowCreate(false); await reload(); }} />}
      {editing && <PriceListForm initial={editing} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await reload(); }} />}

      {lists.length === 0 && !showCreate ? (
        <EmptyState icon={<Tag className="h-8 w-8" aria-hidden />} title={t('empty')} description={t('emptyDescription')}
          action={canWrite ? <Button onClick={() => setShowCreate(true)}>{t('create')}</Button> : undefined} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={lists} rowKey={(l) => l.id} searchPlaceholder={t('columns.name')}
            rowClassName={(l) => (l.id === pricesFor?.id ? 'bg-bg-muted/40' : undefined)} />
        </Card>
      )}

      {pricesFor && <PriceItemsEditor list={pricesFor} canWrite={canWrite} onChanged={() => void reload()} />}
    </div>
  );
}

function PriceListForm({ initial, onCancel, onSaved }: { initial?: PriceList; onCancel: () => void; onSaved: () => void | Promise<void> }): JSX.Element {
  const { t } = useTranslation('pricing');
  const { t: tc } = useTranslation();
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [currency, setCurrency] = useState(initial?.currencyCode ?? '');
  const [adjType, setAdjType] = useState<'none' | 'percent'>(initial?.adjustmentType ?? 'none');
  const [adjValue, setAdjValue] = useState(initial?.adjustmentValue ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [hideFromPos, setHideFromPos] = useState(initial?.hideFromPos ?? false);
  // Both default ON for a new group — it earns like everyone else until told not to.
  const [hasCashback, setHasCashback] = useState(initial?.hasCashback ?? true);
  const [hasRegCashback, setHasRegCashback] = useState(initial?.hasRegistrationCashback ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const body = {
        name: name.trim(),
        currencyCode: currency.trim() ? currency.trim().toUpperCase() : (isEdit ? null : undefined),
        adjustmentType: adjType,
        adjustmentValue: adjType === 'percent' && adjValue.trim() ? adjValue.trim() : (isEdit ? null : undefined),
        note: note.trim() ? note.trim() : (isEdit ? null : undefined),
        hideFromPos,
        hasCashback,
        hasRegistrationCashback: hasRegCashback,
      };
      if (isEdit && initial) await updatePriceList(initial.id, body);
      else await createPriceList({ name: body.name, currencyCode: body.currencyCode ?? undefined, adjustmentType: adjType, adjustmentValue: body.adjustmentValue ?? undefined, note: body.note ?? undefined, hideFromPos, hasCashback, hasRegistrationCashback: hasRegCashback });
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'conflict' ? t('errors.duplicateName') : t('errors.save'));
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">{isEdit ? initial?.name : t('create')}</h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={submit} noValidate>
        <FormField id="pl-name" label={t('form.name')} required>
          <Input id="pl-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </FormField>
        <FormField id="pl-cur" label={t('form.currency')} helperText={t('form.currencyHint')}>
          <CurrencySelect id="pl-cur" value={currency} onChange={setCurrency} includeEmpty emptyLabel={t('form.anyCurrency')} />
        </FormField>
        <FormField id="pl-adjt" label={t('form.adjustmentType')}>
          <select id="pl-adjt" className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg" value={adjType} onChange={(e) => setAdjType(e.target.value as 'none' | 'percent')}>
            <option value="none">{t('adjustmentType.none')}</option>
            <option value="percent">{t('adjustmentType.percent')}</option>
          </select>
        </FormField>
        {adjType === 'percent' && (
          <FormField id="pl-adjv" label={t('form.adjustmentValue')}>
            <Input id="pl-adjv" type="number" step="0.01" value={adjValue} onChange={(e) => setAdjValue(e.target.value)} placeholder="-10" />
          </FormField>
        )}
        <div className="md:col-span-2">
          <FormField id="pl-note" label={t('form.note')}>
            <Input id="pl-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
        </div>
        <div className="md:col-span-2">
          <FormField id="pl-hidepos" label={t('form.posVisibility')} helperText={t('form.hideFromPosHint')}>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                id="pl-hidepos"
                type="checkbox"
                checked={hideFromPos}
                onChange={(e) => setHideFromPos(e.target.checked)}
              />
              <span>{t('form.hideFromPos')}</span>
            </label>
          </FormField>
        </div>
        <div className="md:col-span-2">
          <FormField id="pl-cashback" label={t('form.cashback')} helperText={t('form.cashbackHint')}>
            <div className="flex flex-col gap-1.5">
              <label className="inline-flex items-center gap-2 text-sm">
                <input id="pl-cashback" type="checkbox" checked={hasCashback}
                  onChange={(e) => setHasCashback(e.target.checked)} />
                <span>{t('form.hasCashback')}</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input id="pl-regcashback" type="checkbox" checked={hasRegCashback}
                  onChange={(e) => setHasRegCashback(e.target.checked)} />
                <span>{t('form.hasRegistrationCashback')}</span>
              </label>
            </div>
          </FormField>
        </div>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.save')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

function PriceItemsEditor({ list, canWrite, onChanged }: { list: PriceList; canWrite: boolean; onChanged: () => void }): JSX.Element {
  const { t, i18n } = useTranslation('pricing');
  const { t: tc } = useTranslation();
  const [rows, setRows] = useState<PriceListItem[] | null>(null);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [itemId, setItemId] = useState<string | undefined>(undefined);
  const [minQty, setMinQty] = useState('1');
  const [currency, setCurrency] = useState(list.currencyCode ?? '');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async (): Promise<void> => { setRows(await listPriceListItems(list.id)); };
  useEffect(() => {
    void reload();
    void api().get<{ items: CatalogItem[] }>('/items?limit=500').then((r) => setCatalog(r.data.items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.id]);

  const options = useMemo(() => catalog.map((c) => ({ value: c.id, label: c.name, sublabel: c.code })), [catalog]);
  const valid = !!itemId && Number(minQty) > 0 && Number(price) >= 0 && price.trim() !== '';

  const add = async (): Promise<void> => {
    if (!itemId) return;
    setBusy(true);
    try {
      await upsertPriceListItem(list.id, { itemId, minQty: minQty || '1', unitPrice: price, currencyCode: currency.trim() ? currency.trim().toUpperCase() : undefined });
      toast.success(t('items.saved'));
      setItemId(undefined); setMinQty('1'); setPrice('');
      await reload(); onChanged();
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  const remove = async (rowId: string): Promise<void> => {
    try { await deletePriceListItem(list.id, rowId); await reload(); onChanged(); } catch { toast.error(tc('errors:server')); }
  };

  return (
    <Card>
      <div className="mb-1 flex items-center gap-2">
        <Tag className="h-4 w-4 text-brand" aria-hidden />
        <h2 className="text-lg font-semibold">{t('items.title')} · {list.name}</h2>
      </div>
      <p className="mb-4 text-sm text-fg-muted">{t('items.subtitle')}</p>

      {canWrite && (
        <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-border p-3 sm:grid-cols-12 sm:items-end">
          <div className="sm:col-span-5">
            <label className="mb-1 block text-xs text-fg-muted">{t('items.item')}</label>
            <Combobox options={options} value={itemId} onChange={setItemId} placeholder={t('items.pickItem')} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs text-fg-muted">{t('items.minQty')}</label>
            <Input inputMode="decimal" value={minQty} onChange={(e) => setMinQty(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs text-fg-muted">{t('items.currency')}</label>
            <CurrencySelect value={currency} onChange={setCurrency} includeEmpty emptyLabel={t('form.anyCurrency')} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs text-fg-muted">{t('items.unitPrice')}</label>
            <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>
          <div className="sm:col-span-1">
            <Button onClick={() => void add()} loading={busy} disabled={!valid} className="w-full"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {!rows ? (
        <Skeleton className="h-24 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('items.none')}</p>
      ) : (
        <table className="w-full text-start text-sm">
          <thead className="text-xs text-fg-subtle">
            <tr className="border-b border-border">
              <th className="px-2 py-1.5 text-start">{t('items.item')}</th>
              <th className="px-2 py-1.5 text-end">{t('items.minQty')}</th>
              <th className="px-2 py-1.5 text-start">{t('items.currency')}</th>
              <th className="px-2 py-1.5 text-end">{t('items.unitPrice')}</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60">
                <td className="px-2 py-1.5">{r.itemName ?? '—'}<span className="ms-2 font-mono text-[10px] text-fg-subtle">{r.itemSku}</span></td>
                <td className="px-2 py-1.5 text-end font-mono tabular-nums">{Number(r.minQty)}</td>
                <td className="px-2 py-1.5">{r.currencyCode ?? <span className="text-fg-muted">{t('form.anyCurrency')}</span>}</td>
                <td className="px-2 py-1.5 text-end font-mono tabular-nums">{((c) => c ? formatMoney(r.unitPrice, { locale: i18n.language, currency: c }) : formatDecimals(r.unitPrice, null, i18n.language))(r.currencyCode ?? list.currencyCode)}</td>
                <td className="px-2 py-1.5 text-end">
                  {canWrite && (
                    <Button variant="ghost" size="sm" onClick={() => void remove(r.id)} title={t('items.remove')}>
                      <Trash2 className="h-4 w-4 text-danger" aria-hidden />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
